import type { FC, Ref } from "react";

import { InteractionModeChip } from "components/materials/page/interaction-mode/InteractionModeChip";
import { InteractionModeListbox } from "components/materials/page/interaction-mode/InteractionModeListbox";
import { InteractionMode } from "components/server-types";
import { t } from "i18n/i18n";
import { DualButton } from "mds/components/DualButton";
import { Tooltip } from "mds/components/Tooltip";
import { useCapabilities } from "store/capability-hooks";
import { pageSelectors } from "store/selectors";
import { useObjectSelector } from "store/store-hooks";
import { isGroupPage } from "utils/page-utils";

export type PageOverviewInteractionModeProps = {
  pageGroupIndex: number;
  pageId: string;
  hasSubmissions: boolean;
  canChangeMode: boolean;
  onModeChange?: (mode: InteractionMode) => void;
  onGroupCardToggle?: () => void;
  ref?: Ref<HTMLButtonElement>;
};

export const PageOverviewInteractionMode: FC<PageOverviewInteractionModeProps> = ({
  pageGroupIndex,
  pageId,
  hasSubmissions,
  canChangeMode,
  onModeChange,
  onGroupCardToggle,
  ref,
}) => {
  const page = useObjectSelector((s) => pageSelectors.selectById(s, pageId), ["interaction_mode"]);
  const { canParticipateInSection } = useCapabilities();

  if (!page) {
    return null;
  }

  const modeLocked = !canChangeMode || hasSubmissions;
  const submissionsTooltip =
    hasSubmissions && canChangeMode ? t("tooltip.group_config_has_submissions") : "";

  if (page.interaction_mode === InteractionMode.STATIC) {
    return null;
  }

  if (canParticipateInSection) {
    return (
      <InteractionModeChip
        pageGroupIndex={pageGroupIndex}
        pageId={pageId}
        ref={ref}
        onGroupCardToggle={onGroupCardToggle}
      />
    );
  }

  if (modeLocked) {
    return (
      <Tooltip content={submissionsTooltip} positionStrategy="fixed">
        <InteractionModeChip
          hasTooltip={!!submissionsTooltip}
          pageGroupIndex={pageGroupIndex}
          pageId={pageId}
          ref={ref}
          onGroupCardToggle={isGroupPage(page) ? onGroupCardToggle : undefined}
        />
      </Tooltip>
    );
  }

  if (!onModeChange) {
    return null;
  }

  if (isGroupPage(page)) {
    return (
      <DualButton
        buttonLeft={
          <InteractionModeChip
            pageGroupIndex={pageGroupIndex}
            pageId={pageId}
            ref={ref}
            onGroupCardToggle={onGroupCardToggle}
          />
        }
        buttonRight={
          <InteractionModeListbox pageId={pageId} chevronOnly onModeChange={onModeChange} />
        }
      />
    );
  }

  return <InteractionModeListbox pageId={pageId} ref={ref} onModeChange={onModeChange} />;
};

PageOverviewInteractionMode.displayName = "PageOverviewInteractionMode";
